import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useTranslator } from "@templafy/translation";
import { useUUID } from "../../../../hooks/useUUID.js";
import { combineClasses } from "../../../../utilities/combineClasses.js";
import { InternalStack } from "../../../stack/internal/InternalStack.js";
import { HelpMessage } from "../HelpMessage.js";
import FieldSet_module from "./FieldSet.module.js";
import { FieldsetContext } from "./FieldSetContext.js";
import { Collapsible } from "../../../../transitions/collapsible/Collapsible.js";
import { useFormContext } from "../../FormContext.js";
import { useCombinedRefs } from "../../../../hooks/useCombinedRefs.js";
import { VisuallyHidden } from "../../../visuallyHidden/VisuallyHidden.js";
import { FieldSetToggleButton } from "./FieldSetToggleButton.js";
import { useCallbackRef } from "../../../../hooks/useCallbackRef.js";
import { useDevelopmentEffect } from "../../../../hooks/_internal/useDevelopmentEffect.js";
const FieldSet = /*#__PURE__*/ forwardRef(({ testId, className, style, title, description, disabled, readOnly, required, size, status, children, open, onOpenChange, gap = 'auto', collapsible, ...props }, ref)=>{
    const fieldsetRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, fieldsetRef);
    const submitCounter = useFormContext()?.submitCounter;
    const fieldsetId = useUUID({
        prefix: 'fieldset'
    });
    const descriptionId = useUUID({
        prefix: "fieldset-description"
    });
    const [hasInvalidFields, setHasInvalidFields] = useState(false);
    const translator = useTranslator();
    const isOpenControlled = void 0 !== onOpenChange || void 0 !== open;
    const [isInternalOpen, setIsInternalOpen] = useState(true);
    const isOpen = isOpenControlled ? open : isInternalOpen;
    const onOpenChangeRef = useCallbackRef((nextOpen)=>{
        if (isOpenControlled) onOpenChange?.(nextOpen);
        else setIsInternalOpen(nextOpen);
    });
    useDevelopmentEffect(()=>{
        if (isOpenControlled && void 0 !== collapsible) console.error('[Form.FieldSet]: The `collapsible` prop should not be set when `open` or `onOpenChange` are provided. Ignoring the `collapsible` prop.');
    });
    const isCollapsible = collapsible ?? Boolean(onOpenChange);
    const value = useMemo(()=>({
            disabled,
            readOnly,
            required,
            size,
            status,
            onInputStatusChange: ()=>{
                const invalidInputs = fieldsetRef.current?.querySelectorAll('[aria-invalid="true"]') ?? [];
                if (invalidInputs.length > 0) {
                    setHasInvalidFields(true);
                    onOpenChangeRef.current(true);
                } else setHasInvalidFields(false);
            }
        }), [
        disabled,
        readOnly,
        required,
        size,
        status,
        onOpenChangeRef
    ]);
    useEffect(()=>{
        if (hasInvalidFields) onOpenChangeRef.current(true);
    }, [
        submitCounter
    ]);
    return /*#__PURE__*/ jsx(FieldsetContext.Provider, {
        value: value,
        children: /*#__PURE__*/ jsxs("fieldset", {
            className: combineClasses(className, FieldSet_module.fieldSet),
            style: style,
            "data-testid": testId,
            "aria-describedby": descriptionId,
            disabled: disabled,
            id: fieldsetId,
            "data-collapsed": !isOpen,
            ref: combinedRef,
            children: [
                isCollapsible ? /*#__PURE__*/ jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ jsx("legend", {
                            children: /*#__PURE__*/ jsx(VisuallyHidden, {
                                children: title
                            })
                        }),
                        /*#__PURE__*/ jsx(FieldSetToggleButton, {
                            "aria-controls": fieldsetId,
                            "aria-expanded": isOpen,
                            "aria-label": translator.uiComponentsFieldSetToggleButtonPrefix({
                                title
                            }),
                            onClick: ()=>onOpenChangeRef.current(!isOpen),
                            showDangerIcon: hasInvalidFields,
                            expanded: isOpen,
                            testId: "toggle-button",
                            children: /*#__PURE__*/ jsx("span", {
                                "aria-hidden": true,
                                "data-testid": "title",
                                children: title
                            })
                        })
                    ]
                }) : /*#__PURE__*/ jsx("legend", {
                    "data-testid": "title",
                    className: combineClasses(FieldSet_module.label, FieldSet_module.fieldSetLabelTypography),
                    children: title
                }),
                /*#__PURE__*/ jsxs(Collapsible, {
                    visible: isOpen,
                    children: [
                        description && /*#__PURE__*/ jsx(HelpMessage, {
                            id: descriptionId,
                            testId: "description",
                            children: description
                        }),
                        /*#__PURE__*/ jsx(InternalStack, {
                            className: combineClasses(FieldSet_module.fieldsetContent, 'auto' === gap && FieldSet_module.defaultSpacing),
                            gap: 'auto' === gap ? void 0 : gap,
                            ...props,
                            children: children
                        })
                    ]
                })
            ]
        })
    });
});
FieldSet.displayName = 'FieldSet';
export { FieldSet };
